<template>
  <v-container fluid class="pa-6">
    <!-- KPI Cards Section -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="primary">mdi-barcode-scan</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Referencias Únicas</div>
              <div class="kpi-value">{{ countUniqueItems("sku") }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="info">mdi-package-variant</v-icon>
                {{ inventory_filter.length || 0 }} productos totales
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="success">mdi-tag-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Marcas</div>
              <div class="kpi-value">{{ countUniqueItems("marca") }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="success">mdi-shape</v-icon>
                {{ countUniqueItems("categoria") }} categorías
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="info">mdi-palette-swatch</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Variedad de Color</div>
              <div class="kpi-value">{{ countUniqueItems("color") }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="purple">mdi-ruler</v-icon>
                {{ countUniqueSizes() }} tallas
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="warning">mdi-alert-circle-outline</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Stock Mínimo</div>
              <div class="kpi-value">{{ calculateTotalMinStock() }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="warning">mdi-trending-up</v-icon>
                Unidades de seguridad
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content Card -->
    <v-card class="main-card elevation-0" outlined>
      <!-- Header with Actions -->
      <v-card-title class="pa-0">
        <v-toolbar flat height="auto" class="minimal-toolbar">
          <h2 class="section-title">Maestro de Productos</h2>
          <v-spacer></v-spacer>

          <!-- Search and Filters -->
          <v-autocomplete
            v-model="filters.id_proveedor"
            :items="suppliers"
            label="Proveedor"
            item-value="code"
            item-text="nombre"
            outlined
            hide-details
            return-object
            dense
            clearable
            class="mr-4"
            style="max-width: 250px"
          />

          <v-text-field
            ref="barcodeInput"
            v-model="barcode"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-barcode-scan"
            placeholder="Escanear código de barras"
            class="mr-4"
            style="max-width: 260px"
            @keyup.enter="onBarcodeScanned"
          />

          <v-text-field
            v-model="filters.refe"
            label="Buscar"
            placeholder="Nombre, referencia, EAN, SKU, marca, PLU"
            clearable
            outlined
            @keyup="filterData"
            @keyup.enter="get_inventory"
            hide-details
            dense
            class="mr-4"
            style="max-width: 280px"
          />

          <v-btn
            color="primary"
            @click="get_inventory"
            :disabled="!filters.id_proveedor && !filters.refe"
            class="mr-2"
          >
            <v-icon left>mdi-magnify</v-icon>
            Buscar
          </v-btn>

          <v-btn
            color="primary"
            @click="newForm"
            :disabled="!canCreateProduct()"
            :title="
              !canCreateProduct()
                ? 'Seleccione un proveedor primero'
                : 'Crear nuevo producto'
            "
          >
            <v-icon left>mdi-plus</v-icon>
            Nuevo
          </v-btn>

          <v-btn icon class="ml-2" @click="exportToCSV" title="Exportar a CSV">
            <v-icon>mdi-file-export</v-icon>
          </v-btn>
        </v-toolbar>
      </v-card-title>

      <!-- Data Table (escritorio) -->
      <v-data-table
        v-if="!$vuetify.breakpoint.smAndDown"
        :headers="minimalHeaders"
        :items="inventory_filter"
        :items-per-page="100"
        :search="search"
        item-key="sku"
        :loading="loading_status"
        loader-height="3"
        loading-text="Cargando ..."
        :sort-by="['nombre']"
        :sort-desc="[false]"
        dense
        fixed-header
        height="calc(100vh - 360px)"
        mobile-breakpoint="0"
        class="dense-table clickable-rows"
        :footer-props="{
          'items-per-page-options': [50, 100, 250, 500],
        }"
        @click:row="editItem"
      >
        <!-- Nombre -->
        <template v-slot:[`item.nombre`]="{ item }">
          <span class="cell-strong">{{ item.nombre }}</span>
        </template>

        <!-- Color (swatch + texto) -->
        <template v-slot:[`item.color`]="{ item }">
          <span v-if="item.color">{{ item.color }}</span>
          <span v-else class="grey--text">—</span>
        </template>

        <!-- Talla -->
        <template v-slot:[`item.talla`]="{ item }">
          <v-chip x-small label color="grey lighten-3">{{ item.talla || "—" }}</v-chip>
        </template>

        <!-- Pricing columns -->
        <template v-slot:[`item.costo`]="{ item }">
          <span class="grey--text text--darken-1">{{ formatCurrency(item.costo) }}</span>
        </template>
        <template v-slot:[`item.precio`]="{ item }">
          <span class="cell-strong">{{ formatCurrency(item.precio) }}</span>
        </template>
        <template v-slot:[`item.descuento`]="{ item }">
          <span v-if="item.descuento > 0" class="error--text">{{ formatCurrency(item.descuento) }}</span>
          <span v-else class="grey--text">—</span>
        </template>

        <!-- Stock mínimo -->
        <template v-slot:[`item.cantidad`]="{ item }">
          <span :class="{ 'warning--text': (item.cantidad || 0) > 0 }">{{ item.cantidad || 0 }}</span>
        </template>


        <!-- No data -->
        <template v-slot:no-data>
          <div class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-package-variant</v-icon>
            <h3 class="text-h6 mt-4 grey--text">No hay productos registrados</h3>
            <v-btn color="primary" class="mt-4" @click="newForm">
              <v-icon left>mdi-plus</v-icon>
              Agregar Primer Producto
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Lista (móvil) -->
      <v-data-iterator
        v-else
        :items="inventory_filter"
        :items-per-page="25"
        :search="search"
        item-key="sku"
        :loading="loading_status"
        :sort-by="'nombre'"
        :footer-props="{
          'items-per-page-options': [25, 50, 100],
        }"
      >
        <template v-slot:loading>
          <div class="text-center py-8 grey--text">Cargando ...</div>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-package-variant</v-icon>
            <h3 class="text-h6 mt-4 grey--text">No hay productos registrados</h3>
            <v-btn color="primary" class="mt-4" @click="newForm">
              <v-icon left>mdi-plus</v-icon>
              Agregar Primer Producto
            </v-btn>
          </div>
        </template>

        <template v-slot:default="{ items }">
          <v-list class="py-0">
            <template v-for="item in items">
              <v-list-item :key="item.sku" @click="editItem(item)" class="px-4">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ item.nombre }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.marca }}<span v-if="item.refe"> · Ref: {{ item.refe }}</span>
                    <span v-if="item.color"> · {{ item.color }}</span>
                    <span v-if="item.talla"> · T: {{ item.talla }}</span>
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption grey--text">
                    EAN: {{ item.ean }}<span v-if="item.sku"> · SKU: {{ item.sku }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action class="my-1">
                  <div class="font-weight-bold primary--text">
                    {{ formatCurrency(item.precio) }}
                  </div>
                </v-list-item-action>
              </v-list-item>
              <v-divider :key="`${item.sku}-divider`" />
            </template>
          </v-list>
        </template>
      </v-data-iterator>
    </v-card>

    <!-- Product Dialog -->
    <ProductForm
      :dialog="Product_dialog"
      @close="handleProductClose"
      @refresh="handleProductRefresh"
      @print-labels="printLabels"
      :item="item"
      :newItem="!isEditing"
      :suppliers="suppliers"
      :show-labels="true"
      table="items"
    />

    <!-- SKU Details Dialog -->
    <v-dialog
      v-model="sku_dialogo"
      scrollable
      max-width="1200px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <v-card class="elevation-0">
        <v-toolbar flat class="minimal-toolbar">
          <h3 class="dialog-title">Detalle de SKUs</h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="sku_dialogo = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-form
            v-model="price_valid"
            ref="priceChangeform"
            @submit.prevent="price_change"
          >
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="new_cost"
                  label="Nuevo costo"
                  outlined
                  dense
                  prepend-inner-icon="mdi-currency-usd"
                  clearable
                  @change="update_selected_values"
                  :rules="f_required"
                  hide-details="auto"
                  type="number"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="new_price"
                  label="Nuevo precio"
                  outlined
                  dense
                  prepend-inner-icon="mdi-currency-usd"
                  clearable
                  @change="update_selected_values"
                  :rules="f_required"
                  hide-details="auto"
                  type="number"
                />
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn
                  color="warning"
                  type="submit"
                  :disabled="!price_valid || selected_skus.length === 0"
                  block
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Aplicar cambio
                </v-btn>
              </v-col>
            </v-row>
          </v-form>

          <v-data-table
            v-if="!$vuetify.breakpoint.smAndDown"
            v-model="selected_skus"
            :headers="header_sku"
            :items="sku_lst"
            :items-per-page="10"
            mobile-breakpoint="0"
            class="elevation-1"
            :loading="loading_status"
            loader-height="3"
            loading-text="Cargando ..."
            show-select
            item-key="ean"
            :sort-by="['talla']"
            :sort-desc="[false]"
          >
            <!-- Format pricing columns -->
            <template v-slot:[`item.costo`]="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.costo) }}</span>
            </template>
            <template v-slot:[`item.precio`]="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.precio) }}</span>
            </template>
            <template v-slot:[`item.descuento`]="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.descuento) }}</span>
            </template>
          </v-data-table>

          <!-- Tarjetas (móvil) -->
          <div v-else class="sku-mobile-list">
            <div v-if="!sku_lst.length" class="text-center py-8 grey--text">
              No hay SKUs para mostrar
            </div>
            <v-card
              v-for="item in sku_lst"
              :key="item.ean"
              outlined
              class="mb-2 sku-mobile-card"
              :class="{ 'sku-mobile-card--selected': isSkuSelected(item) }"
              @click="toggleSkuSelection(item)"
            >
              <div class="d-flex align-start pa-3">
                <v-checkbox
                  :input-value="isSkuSelected(item)"
                  hide-details
                  dense
                  class="mt-0 pt-0 mr-2"
                  @click.stop="toggleSkuSelection(item)"
                />
                <div class="flex-grow-1" style="min-width: 0">
                  <div class="d-flex align-center mb-1">
                    <v-chip x-small label color="primary" outlined class="mr-2">
                      {{ item.talla || "—" }}
                    </v-chip>
                    <span v-if="item.color" class="text-body-2">{{ item.color }}</span>
                    <v-spacer />
                    <span class="font-weight-bold primary--text">{{ formatCurrency(item.precio) }}</span>
                  </div>
                  <div class="text-caption grey--text text--darken-1">
                    EAN: {{ item.ean }}<span v-if="item.refe"> · Ref: {{ item.refe }}</span>
                  </div>
                  <div class="text-caption grey--text">
                    {{ item.marca }}<span v-if="item.categoria"> · {{ item.categoria }}</span>
                  </div>
                  <div class="text-caption mt-1">
                    <span class="grey--text">Costo: {{ formatCurrency(item.costo) }}</span>
                    <span v-if="item.descuento > 0" class="error--text ml-3">
                      Desc: {{ formatCurrency(item.descuento) }}
                    </span>
                  </div>
                </div>
              </div>
            </v-card>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6 flex-wrap" style="row-gap: 8px">
          <v-btn
            color="primary"
            outlined
            :disabled="selected_skus.length === 0"
            @click="openLabelDialog"
          >
            <v-icon left>mdi-label-multiple-outline</v-icon>
            Etiquetas ({{ selected_skus.length }})
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="sku_dialogo = false"> Cancelar </v-btn>
          <v-btn
            color="primary"
            text
            @click="update_sku_values"
            :disabled="sku_lst.length === 0"
          >
            <v-icon left>mdi-content-copy</v-icon>
            Aplicar primer valor a todos
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Dialog -->
    <v-dialog v-model="loading_status" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-card-text class="pa-4">
          <v-row align="center" no-gutters>
            <v-col cols="auto" class="mr-3">
              <v-progress-circular indeterminate color="white"></v-progress-circular>
            </v-col>
            <v-col>
              <div class="subtitle-1">Procesando información</div>
              <div class="text-caption">Por favor espere...</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Editor de Etiquetas WYSIWYG (componente compartido con Inventario) -->
    <LabelEditor v-model="showLabelDialog" :items="labelItems" @show-notification="showMessage" />

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday } from "@/services/webserver.js";
import createItem from "@/utils/createProduct.js";
import ProductForm from "@/components/ProductForm.vue";
import LabelEditor from "@/components/LabelEditor.vue";

export default {
  components: { ProductForm, LabelEditor },
  data() {
    return {
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },
      sku_dialogo: false,
      suppliers: [],
      supplier: null,
      Product_dialog: false,
      isEditing: false,
      errors: [],
      valid: false,
      item_dialogo: false,
      editDialog: false,
      edit_price: false,
      edit_discount: false,
      price_valid: false,
      discount_valid: false,
      editValid: false,
      newPrice: 0,
      loading_status: false,
      f_required: [(v) => !!v || "Requerido"],
      f_email: [
        (v) => !!v || "Correo valido",
        (v) => /.+@.+/.test(v) || "Correo no valido",
      ],
      imageUrl: null,
      dialog: false,
      header_sku: [
        {
          text: "EAN",
          align: "start",
          sortable: true,
          filterable: true,
          value: "ean",
          dataType: "text",
        },
        {
          text: "Categoría",
          align: "start",
          sortable: true,
          filterable: true,
          value: "categoria",
          dataType: "text",
        },
        {
          text: "Grupo",
          align: "start",
          sortable: true,
          filterable: true,
          value: "grupo",
          dataType: "text",
        },
        {
          text: "Marca",
          align: "start",
          sortable: true,
          filterable: true,
          value: "marca",
          dataType: "text",
        },
        {
          text: "Referencia",
          align: "start",
          sortable: true,
          filterable: true,
          value: "refe",
          dataType: "text",
        },
        {
          text: "Color",
          align: "start",
          sortable: true,
          filterable: true,
          value: "color",
          dataType: "text",
        },
        {
          text: "Talla",
          align: "start",
          sortable: true,
          filterable: true,
          value: "talla",
          dataType: "text",
        },
        {
          text: "Sistema",
          align: "start",
          sortable: true,
          filterable: true,
          value: "tallas",
          dataType: "text",
        },
        {
          text: "Costo",
          align: "end",
          sortable: true,
          filterable: true,
          value: "costo",
          dataType: "number",
        },
        {
          text: "Precio",
          align: "end",
          sortable: true,
          filterable: true,
          value: "precio",
          dataType: "number",
        },
        {
          text: "Descuento",
          align: "end",
          sortable: true,
          filterable: true,
          value: "descuento",
          dataType: "number",
        },
      ],
      minimalHeaders: [
        { text: "Nombre", value: "nombre", align: "start", width: 200 },
        { text: "PLU", value: "plu", align: "start", width: 90 },
        { text: "SKU", value: "sku", align: "start", width: 130 },
        { text: "EAN", value: "ean", align: "start", width: 130 },
        { text: "Categoría", value: "categoria", align: "start", width: 120 },
        { text: "Grupo", value: "grupo", align: "start", width: 110 },
        { text: "Marca", value: "marca", align: "start", width: 110 },
        { text: "Referencia", value: "refe", align: "start", width: 110 },
        { text: "Color", value: "color", align: "start", width: 90 },
        { text: "Talla", value: "talla", align: "center", width: 80 },
        { text: "Sistema", value: "tallas", align: "start", width: 90 },
        { text: "Costo", value: "costo", align: "end", width: 100 },
        { text: "Precio", value: "precio", align: "end", width: 100 },
        { text: "Desc.", value: "descuento", align: "end", width: 90 },
        { text: "Stock", value: "cantidad", align: "center", width: 80 },
      ],
      header_changes: [
        {
          text: "Categoría",
          align: "start",
          sortable: true,
          filterable: true,
          value: "categoria",
          dataType: "text",
        },
        {
          text: "Grupo",
          align: "start",
          sortable: true,
          filterable: true,
          value: "grupo",
          dataType: "text",
        },
        {
          text: "Referencia",
          align: "start",
          sortable: true,
          filterable: true,
          value: "refe",
          dataType: "text",
        },
        {
          text: "Color",
          align: "start",
          sortable: true,
          filterable: true,
          value: "color",
          dataType: "text",
        },
        {
          text: "Tamaño",
          align: "center",
          sortable: true,
          filterable: true,
          value: "tallas",
          dataType: "text",
        },
        {
          text: "Cantidad",
          align: "center",
          sortable: true,
          filterable: true,
          value: "quantity",
          dataType: "text",
        },
        {
          text: "Precio",
          align: "center",
          sortable: true,
          filterable: true,
          value: "price",
          dataType: "text",
        },
        {
          text: "Nuevo Precio",
          align: "center",
          sortable: true,
          filterable: true,
          value: "newPrice",
          dataType: "text",
        },
        {
          text: "Diferencia",
          align: "center",
          sortable: true,
          filterable: true,
          value: "diferencia",
          dataType: "text",
        },
      ],
      changes_lst: [],
      price_changes_lst: false,
      loading_changes: false,
      inventory_lst: [],
      inventory_filter: [],
      item: createItem(),
      selected: [],
      brands: [],
      categories: [],
      filters: { id_proveedor: null, refe: null, precio: null },
      search: "",
      itemEdit: createItem(),
      editItemData: createItem(),
      compressed_image: null,
      sku_lst: [],
      selected_skus: [],
      new_cost: null,
      new_price: null,
      // Labels WYSIWYG (el editor vive en el componente LabelEditor)
      showLabelDialog: false,
      labelItems: [],
      // Búsqueda por código de barras (EAN)
      barcode: "",
    };
  },
  mounted() {
    this.get_supplier();
  },
  methods: {
    canCreateProduct() {
      return this.filters.id_proveedor !== null;
    },
    handleProductRefresh() {
      // Si hay un proveedor seleccionado, recargar automáticamente la lista
      if (this.filters.id_proveedor) {
        this.get_inventory();
      } else {
        this.showMessage("Producto guardado exitosamente", "success");
      }
    },
    handleProductClose() {
      this.Product_dialog = false;
      // Resetear el item para el próximo uso
      this.item = createItem();

      // Si hay un proveedor seleccionado, recargar automáticamente la lista
      if (this.filters.id_proveedor) {
        this.get_inventory();
      }
    },
    // Método para mostrar mensajes en el snackbar
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    // Formatea números como moneda
    formatCurrency(value) {
      if (!value && value !== 0) return "$ 0";
      return "$ " + parseFloat(value).toLocaleString(2);
    },

    // Cuenta elementos únicos en un campo
    countUniqueItems(field) {
      if (!this.inventory_filter || this.inventory_filter.length === 0) return 0;
      const uniqueValues = new Set(this.inventory_filter.map((item) => item[field]));
      return uniqueValues.size;
    },

    // Calcula el valor del catálogo basado en precios
    calculateCatalogValue() {
      if (!this.inventory_filter || this.inventory_filter.length === 0) return 0;
      return this.inventory_filter.reduce((total, item) => {
        const precio = parseFloat(item.precio || 0);
        return total + precio;
      }, 0);
    },

    // Calcula el total de stock mínimo
    calculateTotalMinStock() {
      if (!this.inventory_filter || this.inventory_filter.length === 0) return 0;
      return this.inventory_filter.reduce((total, item) => {
        return total + parseFloat(item.cantidad || 0);
      }, 0);
    },

    // Cuenta tallas únicas
    countUniqueSizes() {
      if (!this.inventory_filter || this.inventory_filter.length === 0) return 0;
      const uniqueSizes = new Set(this.inventory_filter.map((item) => item.talla));
      return uniqueSizes.size;
    },

    // Get stock color based on quantity
    getStockColor(cantidad) {
      const qty = parseFloat(cantidad || 0);
      if (qty === 0) return "error";
      if (qty < 5) return "warning";
      return "success";
    },

    // Método para exportar a CSV
    exportToCSV() {
      if (!this.inventory_filter || this.inventory_filter.length === 0) {
        this.showMessage("No hay datos para exportar", "warning");
        return;
      }
      this.exportJSONToCSV(this.inventory_filter);
    },

    exportJSONToCSV(data) {
      const headers = [
        "PLU",
        "SKU",
        "EAN",
        "Producto",
        "Categoría",
        "Grupo",
        "Marca",
        "Referencia",
        "Color",
        "Tamaño",
        "Sistema",
        "Costo",
        "Precio",
        "Descuento",
        "Stock Mínimo",
      ];

      const rows = data.map((item) => [
        item.plu || "",
        item.sku || "",
        item.ean || "",
        item.nombre || "",
        item.categoria || "",
        item.grupo || "",
        item.marca || "",
        item.refe || "",
        item.color || "",
        item.talla || "",
        item.tallas || "",
        item.costo || 0,
        item.precio || 0,
        item.descuento || 0,
        item.cantidad || 0,
      ]);

      const csv = [headers, ...rows]
        .map((row) =>
          row.map((cell) => `"${cell.toString().replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `inventario_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      this.showMessage("Archivo exportado exitosamente", "success");
    },

    // Selección manual para la vista de tarjetas en móvil (replica show-select)
    isSkuSelected(item) {
      return this.selected_skus.some((s) => s.ean === item.ean);
    },

    toggleSkuSelection(item) {
      const idx = this.selected_skus.findIndex((s) => s.ean === item.ean);
      if (idx === -1) {
        this.selected_skus.push(item);
      } else {
        this.selected_skus.splice(idx, 1);
      }
    },

    update_selected_values() {
      if (!this.selected_skus.length) return;

      this.selected_skus.forEach((item) => {
        if (this.new_cost) item.costo = parseFloat(this.new_cost);
        if (this.new_price) item.precio = parseFloat(this.new_price);
      });
    },

    update_sku_values() {
      if (this.sku_lst.length === 0) return;
      const firstItem = this.sku_lst[0];

      this.sku_lst.forEach((element) => {
        element.costo = parseFloat(firstItem.costo);
        element.precio = parseFloat(firstItem.precio);
      });

      // También actualizar los elementos seleccionados
      this.selected_skus = [...this.sku_lst];

      this.showMessage("Valores del primer elemento aplicados a todos", "success");
    },

    newForm() {
      this.item = createItem();
      this.isEditing = false;

      // Si hay un proveedor seleccionado, pre-cargarlo en el nuevo producto
      if (this.filters.id_proveedor) {
        this.item.id_proveedor =
          this.filters.id_proveedor.code || this.filters.id_proveedor.id_nit;
      }

      this.Product_dialog = true;
    },

    get_supplier() {
      this.loading_status = true;
      const qry = {
        store: window.profile.store_id,
        table: "proveedores",
        filters: [
          {
            field: "store",
            operator: "=",
            value: window.profile.store_id.substring(0, 6),
          },
        ],
      };
      console.log("get_supplier", qry, window.profile);
      webserver(
        "get_dataTable",
        qry,
        (data) => {
          this.loading_status = false;
          console.log(data);
          this.suppliers = data.map((itm) => ({
            ...itm,
            code: itm.codigo,
            PartyIdentification: itm.id_nit,
            PartyName: itm.nombre,
            AddressLine: itm.direccion,
            CityName: itm.Ciudad,
            client_email: itm.email,
            client_phone: itm.telefono,
          }));
          this.showMessage(`${this.suppliers.length} proveedores cargados`, "success");
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar proveedores: " + error, "error");
        }
      );
    },

    get_inventory() {
      // Permitir buscar sin proveedor si hay texto de busqueda libre.
      if (!this.filters.id_proveedor && !this.filters.refe) {
        this.showMessage("Seleccione un proveedor o ingrese texto a buscar", "warning");
        return;
      }

      // Limpiar lista actual
      this.inventory_lst = [];
      this.inventory_filter = [];

      const qry = {
        store: window.profile.store_id,
        table: "items",
        groupby: "sku",
        filters: [],
      };

      qry.filters.push({
        field: "id_pos",
        operator: "=",
        value: window.profile.store_id.substring(0, 6),
      });

      if (this.filters.id_proveedor) {
        if (this.filters.id_proveedor.code || this.filters.id_proveedor.id_nit) {
          qry.filters.push({
            field: "id_proveedor",
            operator: "=",
            value: this.filters.id_proveedor.code || this.filters.id_proveedor.id_nit,
          });
        } else {
          this.showMessage("Proveedor seleccionado no válido", "warning");
          return;
        }
      }

      // Busqueda multi-campo: el backend debe interpretar `search` como
      // OR sobre nombre, refe, ean, sku, marca, plu.
      if (this.filters.refe) {
        qry.search = this.filters.refe;
        qry.search_fields = ["nombre", "refe", "ean", "sku", "marca", "plu"];
      }

      this.loading_status = true;
      this.showMessage("Actualizando lista de productos...", "info");
      console.log(qry);

      webserver(
        "get_dataTable",
        qry,
        (data) => {
          this.loading_status = false;

          if (data && data.length > 0) {
            // Garantizar SKU no-nulo para el item-key del v-data-table.
            // Datos legacy pueden traer sku/ean vacíos y reventar el render.
            const safeData = data.map((d, idx) => ({
              ...d,
              sku: d.sku || d.ean || `legacy-${idx}`,
            }));
            this.inventory_lst = safeData;
            this.inventory_filter = safeData;
            this.showMessage(`${safeData.length} productos actualizados`, "success");
          } else {
            this.inventory_lst = [];
            this.inventory_filter = [];
            this.showMessage("No se encontraron productos para este proveedor", "info");
          }
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar productos: " + error, "error");
        }
      );
    },

    get_curva(e) {
      if (!e.sku) {
        this.showMessage("No se puede obtener el detalle sin un SKU válido", "warning");
        return;
      }
      const qry = {
        store: window.profile.store_id,
        table: "items",
        filters: [],
      };
      qry.filters.push({
        field: "id_pos",
        operator: "=",
        value: window.profile.store_id.substring(0, 6),
      });
      if (e.sku) {
        qry.filters.push({
          field: "sku",
          operator: "=",
          value: e.sku,
        });
      } else return;

      this.loading_status = true;
      this.showMessage("Cargando detalles del producto...", "info");

      webserver(
        "get_dataTable",
        qry,
        (data) => {
          this.loading_status = false;

          if (data && data.length > 0) {
            this.sku_lst = data;
            this.selected_skus = []; // Limpiar selecciones previas
            this.new_cost = null;
            this.new_price = null;
            this.sku_dialogo = true;
            this.showMessage(`${data.length} SKUs cargados`, "success");
          } else {
            this.sku_lst = [];
            this.showMessage("No se encontraron SKUs para este producto", "warning");
          }
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar SKUs: " + error, "error");
        }
      );
    },

    get_changes() {
      this.loading_changes = true;
      const qry = { store: window.store.store_id, issueDate: getToday() };
      webserver("get_inventory_pc", qry, (data) => {
        this.loading_changes = false;
        this.changes_lst = data.map((itm) => ({
          ...itm,
          diferencia: parseFloat(itm.diferencia),
          price: parseFloat(itm.price),
          quantity: parseFloat(itm.quantity),
          newPrice: parseFloat(itm.newPrice),
        }));
      });
    },

    GetSortOrder(prop) {
      return (a, b) => (a[prop] > b[prop] ? 1 : a[prop] < b[prop] ? -1 : 0);
    },

    getImageURL(marca, refe) {
      const account = window.store.store_id.substring(0, 6);
      return (
        this.compressed_image ||
        `https://moda-stc.com/photos/${account}/${marca}/${refe}.jpg`
      );
    },

    chooseImage() {
      this.$refs.fileInput.click();
    },

    onSelectFile(dat) {
      const input = this.$refs.fileInput;
      const files = input.files;
      if (files && files[0]) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const prct = 800 / img.width;
            const elem = document.createElement("canvas");
            elem.width = img.width * prct;
            elem.height = img.height * prct;
            const ctx = elem.getContext("2d");
            ctx.drawImage(img, 0, 0, img.width * prct, img.height * prct);
            const imageUrl = elem.toDataURL("image/jpeg");
            this.compressed_image = imageUrl;
            dat.picture = files[0].name;
            this.upload_photo(dat.marca, `${dat.refe.toLowerCase()}.jpg`, imageUrl);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(files[0]);
        this.item.picture = files[0].name.toLowerCase();
      }
    },

    upload_photo(marca, picture, data) {
      const qry = { store: window.store.store_id, photo: data, marca, picture };
      webserver("upload_photo", qry, () => {
        this.compressed_image = null;
      });
    },

    // ── ETIQUETAS (el editor vive en LabelEditor.vue) ──────────────────────

    openLabelDialog() {
      if (!this.selected_skus.length) {
        this.showMessage("Seleccione productos para imprimir etiquetas", "warning");
        return;
      }
      this.labelItems = this.selected_skus;
      this.showLabelDialog = true;
    },

    // Imprimir etiquetas para un solo producto (desde ProductForm).
    printLabels(item) {
      if (!item || (!item.ean && !item.sku)) {
        this.showMessage("El producto no tiene SKU/EAN para etiquetar", "warning");
        return;
      }
      this.labelItems = [item];
      this.showLabelDialog = true;
    },

    filterData() {
      const { filters } = this;
      const searchFields = ["nombre", "refe", "ean", "sku", "marca", "plu"];
      const search = (filters.refe || "").toString().trim().toLowerCase();
      this.inventory_filter = this.inventory_lst.filter((item) => {
        // Igualdad por otros filtros (proveedor ya se filtró en backend).
        for (const key of Object.keys(filters)) {
          if (key === "refe" || key === "id_proveedor") continue;
          if (filters[key] === null || filters[key] === "") continue;
          if (item[key] !== filters[key]) return false;
        }
        // Busqueda multi-campo en el texto libre.
        if (!search) return true;
        return searchFields.some((f) => {
          const v = item[f];
          return v !== undefined && v !== null && v.toString().toLowerCase().includes(search);
        });
      });
    },

    editItem(data) {
      this.item = { ...data }; // Crear copia para evitar mutaciones
      this.isEditing = true;
      this.Product_dialog = true;
    },

    // ── BÚSQUEDA POR CÓDIGO DE BARRAS (EAN) ───────────────────────────────
    // Mismo patrón que el ingreso a bodega (IAIn.vue): lookup exacto por ean
    // en el maestro (get_catalog), sin depender del proveedor.
    onBarcodeScanned() {
      const code = (this.barcode || "").trim();
      this.barcode = "";
      if (code) this.findByBarcode(code);
    },

    findByBarcode(code) {
      this.loading_status = true;
      const qry = {
        store: window.store.store_id,
        dbSearch: "inventory",
        filters: [
          {
            field: "id_pos",
            operator: "=",
            value: window.store.store_id.substring(0, 6),
          },
          { field: "ean", operator: "=", value: code },
        ],
      };
      webserver("get_catalog", qry, (data) => {
        this.loading_status = false;
        const found = Array.isArray(data) && data[0] ? data[0] : null;
        if (found) {
          const safe = { ...found, sku: found.sku || found.ean };
          this.inventory_lst = [safe]; // dejar el producto visible en la tabla
          this.inventory_filter = [safe];
          this.editItem(safe); // abrir la ficha del producto
        } else {
          this.showMessage(`Producto con código ${code} no encontrado`, "error");
        }
        this.focusBarcode();
      });
    },

    focusBarcode() {
      this.$nextTick(() => {
        const el = this.$refs.barcodeInput;
        if (el && el.focus) el.focus();
      });
    },

    openEditDialog(item) {
      this.editItemData = { ...item };
      this.editDialog = true;
    },

    closeEditDialog() {
      this.editDialog = false;
    },

    saveEdit() {
      if (this.$refs.editForm.validate()) {
        const index = this.inventory_lst.findIndex(
          (i) => i.ean === this.editItemData.ean
        );
        if (index !== -1) {
          this.inventory_lst.splice(index, 1, this.editItemData);
          this.inventory_filter = [...this.inventory_lst];
          this.editDialog = false;
        }
      }
    },

    discount_change(e) {
      e.preventDefault();
      if (this.$refs.discountChangeform.validate() && this.discount_valid) {
        const qry = {
          store: window.store.store_id,
          newprice: parseInt(this.newPrice),
          products: this.selected,
          userId: window.userId,
        };
        if (qry.newprice > 0) {
          webserver("changeDiscount", qry, () => {
            this.get_inventory();
            this.edit_discount = false;
          });
        }
      }
    },

    price_change(e) {
      e.preventDefault();
      this.price_valid = this.$refs.priceChangeform.validate();

      if (this.price_valid && this.selected_skus.length > 0) {
        // Asegurar que costo/precio de las filas reflejen new_cost/new_price
        // por si el @change de los campos no alcanzó a dispararse.
        this.update_selected_values();

        this.loading_status = true;
        this.showMessage("Aplicando cambios de precio...", "info");
        // UPDATE puntual por (id_pos, sku) — no recrea el producto.
        const qry = {
          store: window.profile.store_id,
          userId: window.profile.user_email,
          products: this.selected_skus.map((s) => ({
            id_pos: s.id_pos,
            sku: s.sku,
            costo: s.costo,
            precio: s.precio,
          })),
        };

        webserver(
          "changeCostPrice",
          qry,
          () => {
            this.loading_status = false;
            this.sku_dialogo = false;
            this.selected_skus = []; // Limpiar selección
            this.new_cost = null;
            this.new_price = null;

            // Actualizar la lista principal
            this.get_inventory();
            this.showMessage("Cambios de precio aplicados con éxito", "success");
          },
          (error) => {
            this.loading_status = false;
            this.showMessage("Error al aplicar cambios de precio: " + error, "error");
          }
        );
      } else {
        if (this.selected_skus.length === 0) {
          this.showMessage("Debe seleccionar al menos un SKU", "warning");
        } else {
          this.showMessage("Por favor complete los campos requeridos", "warning");
        }
      }
    },
  },
  watch: {
    price_changes_lst(visible) {
      if (visible) {
        this.get_changes();
      }
    },
    "filters.id_proveedor"(newValue, oldValue) {
      // Solo recargar automáticamente si cambia el proveedor y ya había uno seleccionado
      if (newValue && oldValue && newValue.code !== oldValue.code) {
        this.get_inventory();
      }
    },
  },
};
</script>

<style scoped>
/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
}

/* Permite que la barra crezca y los campos hagan wrap sin quedar
   recortados/tapados por la tabla en pantallas angostas. */
.minimal-toolbar >>> .v-toolbar__content {
  height: auto !important;
  min-height: 64px;
  flex-wrap: wrap;
  align-items: center;
  padding: 12px 24px;
  row-gap: 10px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.dialog-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

/* Data Table (densa) */
.dense-table {
  border: none !important;
}

.dense-table >>> .v-data-table__wrapper {
  border: none;
}

.dense-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  padding: 6px 10px !important;
  white-space: nowrap;
}

.dense-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
  transition: background-color 0.15s;
}

.dense-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.clickable-rows >>> tbody tr {
  cursor: pointer;
}

.dense-table >>> tbody tr td {
  padding: 2px 10px !important;
  font-size: 0.8rem !important;
  white-space: nowrap;
  height: 32px !important;
}

.cell-strong {
  font-weight: 600;
  color: #333;
}

/* Tarjetas de SKU en móvil */
.sku-mobile-card {
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.sku-mobile-card--selected {
  border-color: #1976d2 !important;
  background: #e3edff;
}

/* Loading */
.dense-table >>> .v-data-table__progress {
  height: 2px !important;
}

/* Responsive */
@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
  }

  .minimal-toolbar {
    flex-wrap: wrap;
    gap: 16px;
  }
}
</style>
